import { showAlert } from "@/lib/alert";
import { getErrorMessage, isNetworkError, OFFLINE_MESSAGE } from "@/lib/errors";
import { useAuthStore } from "@/stores/use-auth-store";
import axios from "axios";
import { router } from "expo-router";
import { Platform } from "react-native";

// Update this to your API URL
const API_URL = process.env.EXPO_PUBLIC_API_URL!;

// Create axios instances
const api = axios.create({
  baseURL: API_URL + "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Request interceptor to add auth token and normalize multipart uploads
api.interceptors.request.use(
  async (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      if (Platform.OS === "web") {
        // Let the browser set multipart/form-data with the correct boundary.
        if (typeof config.headers.delete === "function") {
          config.headers.delete("Content-Type");
        } else {
          delete config.headers["Content-Type"];
        }
      } else {
        config.headers["Content-Type"] = "multipart/form-data";
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isNetworkError(error)) {
      error.message = OFFLINE_MESSAGE;
      return Promise.reject(error);
    }

    if (error.response?.config?.url === "/login" || error.response?.config?.url === "/logout") {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && useAuthStore.getState().token) {
      const logout = useAuthStore.getState().logOut;
      logout();
      showAlert(
        "Session Expired",
        "Your session has expired. Please log in again.",
        [
          {
            text: "OK",
            onPress: () => {
              router.push("/(auth)");
            },
          },
        ],
      );
    }

    return Promise.reject(error);
  },
);

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  if (isNetworkError(error)) {
    return OFFLINE_MESSAGE;
  }

  return getErrorMessage(error, fallback);
}

export default api;
